import { fromJsDate } from "@freightsimple/time/helpers";
import type { LocalDate } from "@freightsimple/time/branded-types";

interface DateRangePreset {
  label: string;
  start: LocalDate;
  end: LocalDate;
}

export interface DateRangePresetGroup {
  label: string;
  presets: DateRangePreset[];
}

function toRange(start: Date, end: Date): { start: LocalDate; end: LocalDate } {
  return { start: fromJsDate(start), end: fromJsDate(end) };
}

export function generatePresets(now: Date): DateRangePresetGroup[] {
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(todayStart);
  yesterday.setDate(yesterday.getDate() - 1);

  const quick: DateRangePreset[] = [
    { label: "Today", ...toRange(todayStart, todayStart) },
    { label: "Yesterday", ...toRange(yesterday, yesterday) },
    {
      label: "Last 7 Days",
      ...toRange(new Date(todayStart.getTime() - 6 * 86400000), todayStart),
    },
    {
      label: "Last 30 Days",
      ...toRange(new Date(todayStart.getTime() - 29 * 86400000), todayStart),
    },
    {
      label: "Last 3 Months",
      ...toRange(
        new Date(
          todayStart.getFullYear(),
          todayStart.getMonth() - 3,
          todayStart.getDate(),
        ),
        todayStart,
      ),
    },
    {
      label: "Last 6 Months",
      ...toRange(
        new Date(
          todayStart.getFullYear(),
          todayStart.getMonth() - 6,
          todayStart.getDate(),
        ),
        todayStart,
      ),
    },
    {
      label: "Last 12 Months",
      ...toRange(
        new Date(
          todayStart.getFullYear() - 1,
          todayStart.getMonth(),
          todayStart.getDate(),
        ),
        todayStart,
      ),
    },
  ];

  const years: DateRangePreset[] = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 4; y--) {
    years.push({
      label: y.toString(),
      ...toRange(new Date(y, 0, 1), new Date(y, 11, 31)),
    });
  }

  const months: DateRangePreset[] = [];
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    months.push({
      label: d.toLocaleString("default", { month: "short", year: "numeric" }),
      ...toRange(d, end),
    });
  }

  const quarters: DateRangePreset[] = [];
  for (let i = 0; i < 12; i++) {
    const qMonth = Math.floor(now.getMonth() / 3) * 3 - i * 3;
    const start = new Date(now.getFullYear(), qMonth, 1);
    const end = new Date(start.getFullYear(), start.getMonth() + 3, 0);
    const qNum = Math.floor(start.getMonth() / 3) + 1;
    quarters.push({
      label: `Q${qNum} ${start.getFullYear()}`,
      ...toRange(start, end),
    });
  }

  return [
    { label: "Quick", presets: quick },
    { label: "Years", presets: years },
    { label: "Months", presets: months },
    { label: "Quarters", presets: quarters },
  ];
}
